import React, { useContext, useEffect, useState } from "react";
import { Button, Select } from "antd";
import CustomTable from "../../components/CustomTable";
import useApi from "../../hooks/useApi";
import useFilter from "../../hooks/useFilter";
import { AppStateContext } from "../../context/AppContext";
import { ShowToast, Severty } from "../../helper/toast";
import { ListTransaction, ListClass, ListBranch, } from "../../constants/API/API";
import moment from "moment";
import TransactionGraph from "./TransactionGraph";

const TRANSACTION_STATUS = [
  { value: "Pending", label: "Pending" },
  { value: "Completed", label: "Completed" },
  { value: "Failed", label: "Failed" },
];

const TRANSACTION_TYPE = [
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Half-Yearly", label: "Half-Yearly" },
  { value: "Yearly", label: "Yearly" },
];

const Transaction = () => {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Transaction Management");

  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [studentList, setStudentList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [filters, setFilters] = useFilter({
    sortField: "createdOn",
    sort: "desc",
    studentId: undefined,
    classId: undefined,
    branchId: undefined,
    payment_Status: undefined,
    transcationType: undefined,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classRes, branchRes] = await Promise.all([
          ListClass(),
          ListBranch(),
        ]);

        setClassList(classRes?.data?.list || []);
        setBranchList(branchRes?.data?.list || []);
      } catch (error) {
        setStudentList([]);
        setClassList([]);
        setBranchList([]);
      }
    };
    fetchData();
  }, []);

  useApi({
    callback: ListTransaction,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    onSuccess: ({ list, totalDocs }) => {
      setData(list);
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const handleFilterChange = (key) => (value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handleExport = () => {
    if (!data || data.length === 0) {
      ShowToast("No data available to export", Severty.WARNING);
      return;
    }

    const headers = [
      "Transaction ID",
      "Student Name",
      "Class",
      "Branch",
      "Amount",
      "Remarks",
      "Payment Type",
      "Payment Method",
      "Status",
      "Created At",
    ];

    const csvData = data.map((record) => [
      record.transcationId || "-",
      record.studentId?.name || "-",
      record.classId?.name || "-",
      record.branchId?.name || "-",
      record.amount || "-",
      record.remarks || "-",
      record.transcationType || "-",
      record.paymentMethod || "-",
      record.payment_Status || "-",
      record.createdAt ? new Date(record.createdAt).toLocaleDateString() : "-",
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "transactions_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    ShowToast("Report exported successfully", Severty.SUCCESS);
  };

  const renderFilters = () => (
    <>
      <div className="role-wrap">
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Branch"
          onChange={handleFilterChange("branchId")}
          value={filters.branchId}
          options={branchList.map((branch) => ({
            label: branch.name,
            value: branch._id,
          }))}
        />
      </div>
      <div className="role-wrap">
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Status"
          onChange={handleFilterChange("payment_Status")}
          value={filters.payment_Status}
          options={TRANSACTION_STATUS}
        />
      </div>
      <div className="role-wrap">
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Payment Type"
          onChange={handleFilterChange("transcationType")}
          value={filters.transcationType}
          options={TRANSACTION_TYPE}
        />
      </div>
      <div className="role-wrap">
        <Button type="primary" onClick={handleExport}>
          Export Report
        </Button>
      </div>
    </>
  );

  const columns = [
    {
      title: "Transaction ID",
      dataIndex: "transcationId",
      key: "transcationId",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => record?.userId?.name || "-",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      render: (text, record) =>
        record?.userId?.mobileNumber
          ? `+${record?.userId?.countryCode} ${record?.userId?.mobileNumber}`
          : "-",
    },
    // {
    //   title: "Class",
    //   dataIndex: "classId",
    //   key: "classId",
    //   render: (text, record) => record?.classId?.name || "-",
    // },
    {
      title: "Branch",
      dataIndex: "branchId",
      key: "branchId",
      render: (text, record) => record?.branchId?.name || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "remarks",
      dataIndex: "remarks",
      key: "remarks",
    },
    {
      title: "Payment Type",
      dataIndex: "transcationType",
      key: "transcationType",
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "payment_Status",
      key: "payment_Status",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (text ? moment(text).format("ll") : "-"),
    },
  ];

  return (
    <>
      <TransactionGraph />

      <CustomTable
        data={data}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        totalDocs={totalDocs}
        searchField={["name"]}
        searchPlaceholder={"Search by Student Name"}
        customFilter={renderFilters}
        hideAddButton
      />
    </>
  );
};

export default Transaction;